import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function AppTermsOfService() {
    const navigate = useNavigate();
    const lastUpdated = 'January 21, 2026';

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Terms of Service</h1>
            </div>

            <p className="text-sm text-[#7B8C9B] mb-6">Last updated: {lastUpdated}</p>

            <div className="bg-white dark:bg-dark-card rounded-2xl p-5 shadow-sm space-y-6">
                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        1. Acceptance of Terms
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        By accessing or using Way2Will ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. We reserve the right to update these terms at any time, and your continued use of the Service constitutes acceptance of any changes.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        2. Description of Service
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        Way2Will is an AI-powered productivity assistant that helps you manage tasks, schedule events, and organize your daily activities through voice and text commands. The Service includes task management, calendar integration, voice capture, and AI-assisted scheduling features.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        3. User Accounts
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        You must create an account to use certain features of the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and complete information when creating your account and to update this information as necessary.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        4. Subscription and Payments
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        Way2Will offers both free and premium subscription plans. Premium features require a paid subscription. Payments are processed through secure third-party payment processors. Subscriptions automatically renew unless cancelled before the renewal date. Refunds are handled on a case-by-case basis in accordance with our refund policy.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        5. Acceptable Use
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed mb-2">
                        You agree not to:
                    </p>
                    <ul className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary list-disc list-inside space-y-1">
                        <li>Use the Service for any illegal purpose</li>
                        <li>Attempt to gain unauthorized access to the Service</li>
                        <li>Interfere with or disrupt the Service</li>
                        <li>Upload malicious content or code</li>
                        <li>Violate any applicable laws or regulations</li>
                        <li>Resell or redistribute the Service without authorization</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        6. Intellectual Property
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        The Service and its original content, features, and functionality are owned by Way2Will and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of any content you create using the Service.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        7. Third-Party Integrations
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        The Service may integrate with third-party services such as Google Calendar, Gmail, Slack, and others. Your use of these integrations is subject to the respective third-party terms of service. We are not responsible for the availability, accuracy, or content of third-party services.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        8. Disclaimer of Warranties
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, secure, or error-free. AI-generated suggestions and scheduling recommendations are for informational purposes and should be verified by the user.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        9. Limitation of Liability
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        To the maximum extent permitted by law, Way2Will shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service. Our total liability shall not exceed the amount you paid for the Service in the twelve months prior to the claim.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        10. Termination
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        We may terminate or suspend your account at any time for violation of these terms. You may also delete your account at any time. Upon termination, your right to use the Service will immediately cease, and we may delete your data in accordance with our data retention policies.
                    </p>
                </section>

                <section>
                    <h2 className="text-lg font-semibold text-[#1A2832] dark:text-white mb-2">
                        11. Contact Information
                    </h2>
                    <p className="text-sm text-[#4A5C6A] dark:text-dark-text-secondary leading-relaxed">
                        For questions about these Terms of Service, please contact us at:
                    </p>
                    <a
                        href="mailto:support@way2will.com"
                        className="text-[#0D5F6B] font-medium text-sm mt-2 inline-block"
                    >
                        support@way2will.com
                    </a>
                </section>
            </div>
        </div>
    );
}
